import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getAllPermissions,
  getPermissionById,
  createPermission,
  updatePermission,
  deletePermission,
  getAllPermissionSets,
  createPermissionSet,
  updatePermissionSet,
  deletePermissionSet,
  assignPermissionToUser,
  assignPermissionToRole,
  assignPermissionSetToUser,
  assignPermissionSetToRole,
  assignPermissionToPermissionSet,
  getUserEffectivePermissions,
  getRoleEffectivePermissions,
  Permission,
  PermissionResponse,
  PermissionSet,
  PermissionSetResponse,
  CreatePermissionPayload,
  UpdatePermissionPayload,
  CreatePermissionSetPayload,
  UpdatePermissionSetPayload,
  AssignPermissionToUserPayload,
  AssignPermissionToRolePayload,
  AssignPermissionSetToUserPayload,
  AssignPermissionSetToRolePayload,
  EffectivePermissions
} from '../../services/AdminServices/PermissionsServices/permissionsService';
import { toast } from 'react-hot-toast';
import { message } from 'antd';

interface PermissionsState {
  permissions: Permission[];
  permissionSets: PermissionSet[];
  selectedPermission: Permission | null;
  selectedPermissionSet: PermissionSet | null;
  effectivePermissions: EffectivePermissions | null;
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const initialState: PermissionsState = {
  permissions: [],
  permissionSets: [],
  selectedPermission: null,
  selectedPermissionSet: null,
  effectivePermissions: null,
  loading: false,
  error: null,
  totalPages: 0,
  currentPage: 1,
  totalCount: 0,
};

// Async Thunks for Permissions
export const fetchPermissions = createAsyncThunk<
  PermissionResponse,
  { pageNo?: number; pageSize?: number },
  { rejectValue: string }
>('permissions/fetchPermissions', async ({ pageNo = 1, pageSize = 10 }, { rejectWithValue }) => {
  try {
    return await getAllPermissions(pageNo, pageSize);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch permissions');
  }
});

export const fetchPermissionById = createAsyncThunk<
  Permission,
  number,
  { rejectValue: string }
>('permissions/fetchPermissionById', async (permissionId, { rejectWithValue }) => {
  try {
    return await getPermissionById(permissionId);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch permission');
  }
});

export const addPermission = createAsyncThunk<
  Permission,
  CreatePermissionPayload,
  { rejectValue: string }
>('permissions/addPermission', async (payload, { rejectWithValue }) => {
  try {
    const result = await createPermission(payload);
    message.success('Permission created successfully!');
    return result;
  } catch (error: any) {
    message.error('Failed to create permission');
    return rejectWithValue(error.message || 'Failed to create permission');
  }
});

export const editPermission = createAsyncThunk<
  Permission,
  UpdatePermissionPayload,
  { rejectValue: string }
>('permissions/editPermission', async (payload, { rejectWithValue }) => {
  try {
    const result = await updatePermission(payload);
    message.success('Permission updated successfully!');
    return result;
  } catch (error: any) {
    message.error('Failed to update permission');
    return rejectWithValue(error.message || 'Failed to update permission');
  }
});

export const removePermission = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('permissions/removePermission', async (permissionId, { rejectWithValue }) => {
  try {
    await deletePermission(permissionId);
    message.success('Permission deleted successfully!');
    return permissionId;
  } catch (error: any) {
    message.error('Failed to delete permission');
    return rejectWithValue(error.message || 'Failed to delete permission');
  }
});

// Async Thunks for Permission Sets
export const fetchPermissionSets = createAsyncThunk<
  PermissionSetResponse,
  { pageNo?: number; pageSize?: number },
  { rejectValue: string }
>('permissions/fetchPermissionSets', async ({ pageNo = 1, pageSize = 10 }, { rejectWithValue }) => {
  try {
    return await getAllPermissionSets(pageNo, pageSize);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch permission sets');
  }
});

export const addPermissionSet = createAsyncThunk<
  PermissionSet,
  CreatePermissionSetPayload,
  { rejectValue: string }
>('permissions/addPermissionSet', async (payload, { rejectWithValue }) => {
  try {
    const result = await createPermissionSet(payload);
    message.success('Permission set created successfully!');
    return result;
  } catch (error: any) {
    message.error('Failed to create permission set');
    return rejectWithValue(error.message || 'Failed to create permission set');
  }
});

export const editPermissionSet = createAsyncThunk<
  PermissionSet,
  UpdatePermissionSetPayload,
  { rejectValue: string }
>('permissions/editPermissionSet', async (payload, { rejectWithValue }) => {
  try {
    const result = await updatePermissionSet(payload);
    message.success('Permission set updated successfully!');
    return result;
  } catch (error: any) {
    message.error('Failed to update permission set');
    return rejectWithValue(error.message || 'Failed to update permission set');
  }
});

export const removePermissionSet = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('permissions/removePermissionSet', async (permissionSetId, { rejectWithValue }) => {
  try {
    await deletePermissionSet(permissionSetId);
    message.success('Permission set deleted successfully!');
    return permissionSetId;
  } catch (error: any) {
    message.error('Failed to delete permission set');
    return rejectWithValue(error.message || 'Failed to delete permission set');
  }
});

// Assignment Thunks
export const assignPermToUser = createAsyncThunk<
  void,
  AssignPermissionToUserPayload,
  { rejectValue: string }
>('permissions/assignPermToUser', async (payload, { rejectWithValue }) => {
  try {
    await assignPermissionToUser(payload);
    message.success('Permission assigned to user successfully!');
  } catch (error: any) {
    message.error('Failed to assign permission to user');
    return rejectWithValue(error.message || 'Failed to assign permission to user');
  }
});

export const assignPermToRole = createAsyncThunk<
  void,
  AssignPermissionToRolePayload,
  { rejectValue: string }
>('permissions/assignPermToRole', async (payload, { rejectWithValue }) => {
  try {
    await assignPermissionToRole(payload);
    message.success('Permission assigned to role successfully!');
  } catch (error: any) {
    message.error('Failed to assign permission to role');
    return rejectWithValue(error.message || 'Failed to assign permission to role');
  }
});

export const assignPermSetToUser = createAsyncThunk<
  void,
  AssignPermissionSetToUserPayload,
  { rejectValue: string }
>('permissions/assignPermSetToUser', async (payload, { rejectWithValue }) => {
  try {
    await assignPermissionSetToUser(payload);
    message.success('Permission set assigned to user successfully!');
  } catch (error: any) {
    message.error('Failed to assign permission set to user');
    return rejectWithValue(error.message || 'Failed to assign permission set to user');
  }
});

export const assignPermSetToRole = createAsyncThunk<
  void,
  AssignPermissionSetToRolePayload,
  { rejectValue: string }
>('permissions/assignPermSetToRole', async (payload, { rejectWithValue }) => {
  try {
    await assignPermissionSetToRole(payload);
    message.success('Permission set assigned to role successfully!');
  } catch (error: any) {
    message.error('Failed to assign permission set to role');
    return rejectWithValue(error.message || 'Failed to assign permission set to role');
  }
});

export const assignPermToPermSet = createAsyncThunk<
  void,
  { permissionSetId: number; permissionId: number },
  { rejectValue: string }
>('permissions/assignPermToPermSet', async ({ permissionSetId, permissionId }, { rejectWithValue }) => {
  try {
    await assignPermissionToPermissionSet(permissionSetId, permissionId);
    message.success('Permission assigned to permission set successfully!');
  } catch (error: any) {
    message.error('Failed to assign permission to permission set');
    return rejectWithValue(error.message || 'Failed to assign permission to permission set');
  }
});

export const fetchUserEffectivePermissions = createAsyncThunk<
  EffectivePermissions,
  number,
  { rejectValue: string }
>('permissions/fetchUserEffectivePermissions', async (userId, { rejectWithValue }) => {
  try {
    return await getUserEffectivePermissions(userId);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch user effective permissions');
  }
});

export const fetchRoleEffectivePermissions = createAsyncThunk<
  EffectivePermissions,
  number,
  { rejectValue: string }
>('permissions/fetchRoleEffectivePermissions', async (roleId, { rejectWithValue }) => {
  try {
    return await getRoleEffectivePermissions(roleId);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch role effective permissions');
  }
});

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedPermission: (state, action: PayloadAction<Permission | null>) => {
      state.selectedPermission = action.payload;
    },
    setSelectedPermissionSet: (state, action: PayloadAction<PermissionSet | null>) => {
      state.selectedPermissionSet = action.payload;
    },
    clearEffectivePermissions: (state) => {
      state.effectivePermissions = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload.data;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch permissions';
      })
      
      // Fetch Permission by ID
      .addCase(fetchPermissionById.fulfilled, (state, action) => {
        state.selectedPermission = action.payload;
      })
      
      // Add Permission
      .addCase(addPermission.fulfilled, (state, action) => {
        state.permissions.push(action.payload);
      })
      
      // Edit Permission
      .addCase(editPermission.fulfilled, (state, action) => {
        const index = state.permissions.findIndex(p => p.permissionId === action.payload.permissionId);
        if (index !== -1) {
          state.permissions[index] = action.payload;
        }
        if (state.selectedPermission?.permissionId === action.payload.permissionId) {
          state.selectedPermission = action.payload;
        }
      })
      
      // Remove Permission
      .addCase(removePermission.fulfilled, (state, action) => {
        state.permissions = state.permissions.filter(p => p.permissionId !== action.payload);
        if (state.selectedPermission?.permissionId === action.payload) {
          state.selectedPermission = null;
        }
      })
      
      // Fetch Permission Sets
      .addCase(fetchPermissionSets.fulfilled, (state, action) => {
        state.permissionSets = action.payload.data;
      })
      
      // Add Permission Set
      .addCase(addPermissionSet.fulfilled, (state, action) => {
        state.permissionSets.push(action.payload);
      })
      
      // Edit Permission Set
      .addCase(editPermissionSet.fulfilled, (state, action) => {
        const index = state.permissionSets.findIndex(ps => ps.permissionSetId === action.payload.permissionSetId);
        if (index !== -1) {
          state.permissionSets[index] = action.payload;
        }
        if (state.selectedPermissionSet?.permissionSetId === action.payload.permissionSetId) {
          state.selectedPermissionSet = action.payload;
        }
      })
      
      // Remove Permission Set
      .addCase(removePermissionSet.fulfilled, (state, action) => {
        state.permissionSets = state.permissionSets.filter(ps => ps.permissionSetId !== action.payload);
        if (state.selectedPermissionSet?.permissionSetId === action.payload) {
          state.selectedPermissionSet = null;
        }
      })
      
      // Effective Permissions
      .addCase(fetchUserEffectivePermissions.fulfilled, (state, action) => {
        state.effectivePermissions = action.payload;
      })
      .addCase(fetchRoleEffectivePermissions.fulfilled, (state, action) => {
        state.effectivePermissions = action.payload;
      });
  },
});

export const { 
  clearError, 
  setSelectedPermission, 
  setSelectedPermissionSet, 
  clearEffectivePermissions 
} = permissionsSlice.actions;

export default permissionsSlice.reducer;